import { AppetiteType } from '@risksmart-app/domain/src/types/consts/appetite-type';
import type { InsertAppetiteMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertAppetiteDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { InsertAppetiteInput, InsertAppetiteRequest } from './types';

export const useInsertAppetite = createMutationHook({
  name: 'insertAppetite',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.appetite.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (variables: InsertAppetiteInput): InsertAppetiteRequest => {
    const commonInput = {
      ParentIds: variables.ParentIds,
      Statement: variables.Statement,
      EffectiveDate: variables.EffectiveDate,
      CustomAttributeData: variables.CustomAttributeData,
    };

    switch (variables.AppetiteType) {
      case AppetiteType.Impact:
        if (variables.ImpactAppetite == null || !variables.ImpactId) {
          throw new Error(
            'Impact appetite and impact are required for an impact appetite'
          );
        }

        return {
          ...commonInput,
          AppetiteType: variables.AppetiteType,
          ImpactAppetite: variables.ImpactAppetite,
          ImpactId: variables.ImpactId,
        };
      case AppetiteType.Likelihood:
        return {
          ...commonInput,
          AppetiteType: variables.AppetiteType,
          LikelihoodAppetite: variables.LikelihoodAppetite,
        };
      case AppetiteType.Risk:
        return {
          ...commonInput,
          AppetiteType: variables.AppetiteType,
          LowerAppetite: variables.LowerAppetite,
          UpperAppetite: variables.UpperAppetite,
        };
    }
  },
  invalidate: (trpc) => [
    // COI-889: router-level key — the hand-listed set missed
    // `getAppetitesGroupedByImpact` (both siblings have it) and `getAppetites`.
    trpc.frontend.appetite.pathKey(),
    // COI-471 parity: Apollo twin evicts the `risk` field (appetites
    // surface on the linked risk).
    trpc.frontend.risk.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertAppetiteMutation => ({
    insertChildAppetite: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertAppetiteDocument,
  graphqlVariables: (variables: InsertAppetiteInput) => ({
    ...variables,
  }),
  evictFields: ['appetite', 'appetite_aggregate', 'risk_aggregate'],
});
